<template>
    <div
        v-if="sections"
        class="checkbox-tree-container"
    >
        <div
            v-for="(section, key) in applicableSections"
            :key="key"
            class="section-select"
            :style="indent"
            :class="{
                optional: section.optional,
                selected: isChecked(section),
                'children-unselected': !allChildrenChecked(section),
                disable: status.working,
                working: section.isWorking,
                rendered: section.isRendered,
                created: section.isCreated,
                loaded: section.isLoaded,
                'parent-section': sectionHasChildren(section)
            }"
            @click.stop="() => {
                const print = section.print = (section.optional) ? !section.print : true;
                setCheckedStateRecursive(section, print);
            }"
        >
            <div class="section-content">
                <input
                    v-if="section.optional"
                    :id="`checkbox-${section.id}`"
                    class="section-select-checkbox"
                    type="checkbox"
                    :checked="isChecked(section)"
                    @click.stop=""
                    :indeterminate.prop="isChecked(section) && !allChildrenChecked(section)"
                >
                <label
                    :for="`checkbox-${section.id}`"
                    class="section-select-label"
                >
                    {{ section.title }}
                </label>
                <div
                    v-if="sectionHasChildren(section)"
                    class="section-select-collapse-container"
                >
                    <svg
                        class="section-select-collapse-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="#055A94"
                        width="18px"
                        height="18px"
                        @click.stop="toggleShowChildren(section)"
                    >
                        <path
                            d="M0 0h24v24H0z"
                            fill="none"
                        />
                        <path
                            v-if="section.showChildren"
                            d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z"
                        />
                        <path
                            v-if="!section.showChildren"
                            d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"
                        />
                    </svg>
                </div>
            </div>
            <checkbox-tree
                v-if="sectionHasChildren(section) && section.showChildren"
                :sections="sectionChildren(section)"
                :status="status"
                :depth="depth + 1"
            />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';

export default {
    name: 'CheckboxTree',
    props: {
        sections: {
            type: Object,
            required: true
        },
        status: {
            type: Object,
            required: true
        },
        depth: {
            type: Number,
            default: 0
        }
    },
    computed: {
        applicableSections() {
            return Object.values(this.sections)
                .filter((section) => section.optional || this.sectionHasChildren(section));
        },
        indent() {
            return { paddingLeft: (this.depth === 0) ? '0' : '20px' };
        }
    },
    methods: {
        isChecked(section) {
            if (this.sectionHasChildren(section)) {
                const allChildren = this.sectionChildren(section);
                // eslint-disable-next-line no-param-reassign
                section.print = Object.values(allChildren).some((sub) => sub.print);
                if (allChildren !== section.sections) {
                    // For single section categories, need to toggle off
                    _.map(section.sections, subsection => {
                        // eslint-disable-next-line no-param-reassign
                        subsection.print = section.print;
                    });
                }
            }
            return section.print;
        },
        allChildrenChecked(section) {
            if (this.sectionHasChildren(section)) {
                const allChildren = this.sectionChildren(section);
                return section.print && Object.values(allChildren).every((sub) => this.allChildrenChecked(sub));
            }
            return section.print;
        },
        sectionHasChildren(section) {
            if (!section.sections || Object.keys(section.sections).length === 0) {
                return false;
            }
            if (Object.values(section.sections).some((value) => value.optional)) {
                return true;
            }
            return _.findKey(section.sections, (child) => this.sectionHasChildren(child));
        },
        sectionChildren(section) {
            if (Object.values(section.sections).some((value) => value.optional)) {
                return section.sections;
            }
            // Check for sections of children
            const parentKey = _.findKey(section.sections, (child) => this.sectionHasChildren(child));
            if (!parentKey) {
                return false;
            }
            return section.sections[parentKey].sections;
        },
        setCheckedStateRecursive(section, print) {
            /* eslint-disable-next-line no-param-reassign */
            section.print = print;
            if (section.sections) {
                const allChildren = this.sectionChildren(section);
                Object.values(allChildren).forEach((subsection) => {
                    this.setCheckedStateRecursive(subsection, print);
                });
                if (allChildren !== section.sections) {
                    // For single section categories, need to toggle off
                    Object.values(section.sections).forEach((subsection) => {
                        this.setCheckedStateRecursive(subsection, print);
                    });
                }
            }
        },
        toggleShowChildren(section) {
            // eslint-disable-next-line no-param-reassign
            section.showChildren = !section.showChildren;
        }
    }
};
</script>

<style scoped>
.checkbox-tree-container {
    @apply flex flex-col w-full;
}

.section-select {
    @apply flex w-full items-center justify-start text-sm bg-neutral-5 text-print-1 py-1;
}

.section-select.optional {
    @apply cursor-pointer opacity-75;
}
.section-select.selected {
    @apply opacity-100 text-primary-2;
}

.section-select.children-unselected > .section-content {
    @apply opacity-75;
}

.section-select.working {
    @apply opacity-25 text-print-1;
}
.section-select.created {
    @apply opacity-50;
}
.section-select.loaded {
    @apply opacity-75;
}
.section-select.rendered {
    @apply opacity-100;
}

.section-select.disable {
    @apply pointer-events-none;
}

.section-select.parent-section {
    @apply flex-col items-start my-1;
}

.section-select.subsection {
    @apply pl-4;
}

.section-content {
    @apply flex flex-row items-center justify-start w-full px-2 select-none;
}

.section-select-checkbox {
    @apply mr-2 cursor-pointer pointer-events-none;
}

.section-select-label {
    @apply pointer-events-none mr-2;
    margin-bottom: 0 !important;
}

.section-select-collapse-container {
    @apply inline-flex h-full flex-grow justify-end cursor-pointer;
}

.section-select-collapse-icon {
    @apply bg-neutral-5;
}

.section-select-collapse-icon:hover {
    @apply bg-neutral-4;
}
</style>
